import { useState } from 'react';
import { Palette, Menu, X } from 'lucide-react';
import ThemeSwitcher from './components/ThemeSwitcher';
import TypographySection from './components/sections/TypographySection';
import ButtonsSection from './components/sections/ButtonsSection';
import CardsSection from './components/sections/CardsSection';
import ColorsSection from './components/sections/ColorsSection';
import SpacingSection from './components/sections/SpacingSection';
import FormsSection from './components/sections/FormsSection';
import IconsSection from './components/sections/IconsSection';
import AnimationsSection from './components/sections/AnimationsSection';
import { useUIPlaygroundSettings } from './hooks/useUIPlaygroundSettings';

/**
 * UI Playground - DEV-ONLY Design Testing Module
 *
 * Purpose: Safe environment for testing UI improvements
 * - Side-by-side design comparisons
 * - iOS-inspired "sharper" design experiments
 * - Theme switching (light/dark)
 * - Component variant testing
 *
 * WARNING: Development use only. Hidden in production via feature flag.
 */
export default function UIPlaygroundModule() {
  const [activeSection, setActiveSection] = useState('typography');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { settings, updateSettings } = useUIPlaygroundSettings();

  const sections = [
    { id: 'typography', label: 'Typography', component: TypographySection },
    { id: 'buttons', label: 'Buttons', component: ButtonsSection },
    { id: 'cards', label: 'Cards', component: CardsSection },
    { id: 'colors', label: 'Colors', component: ColorsSection },
    { id: 'spacing', label: 'Spacing', component: SpacingSection },
    { id: 'forms', label: 'Forms', component: FormsSection },
    { id: 'icons', label: 'Icons', component: IconsSection },
    { id: 'animations', label: 'Animations', component: AnimationsSection },
  ];

  const ActiveComponent = sections.find(s => s.id === activeSection)?.component;

  return (
    <div className={`min-h-screen ${settings.theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className={`border-b ${settings.theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Palette className={settings.theme === 'dark' ? 'text-blue-400' : 'text-blue-600'} size={28} />
              <div>
                <h1 className={`text-2xl font-bold ${settings.theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  UI Playground
                </h1>
                <p className={`text-sm ${settings.theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Development Design Laboratory
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <ThemeSwitcher
                theme={settings.theme}
                onToggle={() => updateSettings({ theme: settings.theme === 'dark' ? 'light' : 'dark' })}
              />

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`md:hidden p-2 rounded-lg ${
                  settings.theme === 'dark' ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto">
        <div className="flex">
          {/* Desktop Sidebar */}
          <aside className={`hidden md:block w-64 border-r ${
            settings.theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          } min-h-screen`}>
            <nav className="p-4 space-y-1">
              {sections.map(section => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full text-left px-4 py-2.5 rounded-lg font-medium transition-colors ${
                    activeSection === section.id
                      ? settings.theme === 'dark'
                        ? 'bg-blue-600 text-white'
                        : 'bg-blue-100 text-blue-900'
                      : settings.theme === 'dark'
                        ? 'text-gray-300 hover:bg-gray-700'
                        : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {section.label}
                </button>
              ))}
            </nav>
          </aside>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden fixed inset-0 z-50 bg-black bg-opacity-50" onClick={() => setMobileMenuOpen(false)}>
              <div
                className={`w-64 h-full ${settings.theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}
                onClick={e => e.stopPropagation()}
              >
                <nav className="p-4 space-y-1">
                  {sections.map(section => (
                    <button
                      key={section.id}
                      onClick={() => {
                        setActiveSection(section.id);
                        setMobileMenuOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 rounded-lg font-medium transition-colors ${
                        activeSection === section.id
                          ? settings.theme === 'dark'
                            ? 'bg-blue-600 text-white'
                            : 'bg-blue-100 text-blue-900'
                          : settings.theme === 'dark'
                            ? 'text-gray-300 hover:bg-gray-700'
                            : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {section.label}
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          )}

          {/* Main Content */}
          <main className="flex-1 p-6">
            {ActiveComponent && <ActiveComponent theme={settings.theme} />}
          </main>
        </div>
      </div>
    </div>
  );
}
